'use client';

import { LoadingSpinner } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import {
  presetToTTSVoice,
  useGetVoicePresetsApi,
  VoicePresetCard,
} from '@/entities/voice-preset';
import type { VoicePreset } from '@/entities/voice-preset';
import { DtoUserVoicePresetType } from '@/shared/constants';
import { useVoicePresetPreview } from '@/shared/hooks/useVoicePresetPreview';
import { VideoEditorTTSVoice } from '@/shared/apis/tts/get-tts-voices';
import { TTSVoice } from './TtsDialogContext';

export interface TtsDialogMyVoiceTabProps {
  /** isOperator 게이팅을 통과한 가시 voices (TtsDialog 가 이미 필터링한 목록). */
  visibleVoices: VideoEditorTTSVoice[];
  /** 카드 선택 → 즉시 onConfirm + 다이얼로그 닫기. payload → TTSVoice 변환 책임. */
  onPick: (voice: TTSVoice) => void;
  /** 사전 선택 시각 강조 (border primary). 매칭 카드만 highlighted. */
  defaultPresetId?: number;
}

/**
 * TtsDialog '내 목소리' 탭 (Phase B 단계 5, MS-7).
 *
 * 카드 그리드(`mode='pick'`) 에서 클릭 시:
 *   1. payload.code 와 매칭되는 VideoEditorTTSVoice 를 visibleVoices 에서 lookup
 *   2. 매칭 없으면 무시 (BE voice 목록 변경 + payload stale 케이스)
 *   3. presetToTTSVoice 로 변환 후 onPick — TtsDialog 가 onConfirm + 닫기 처리
 *
 * 미리듣기는 `useVoicePresetPreview` 훅으로 `/my-voice` 와 동일 매니저 공유.
 */
export default function TtsDialogMyVoiceTab({
  visibleVoices,
  onPick,
  defaultPresetId,
}: TtsDialogMyVoiceTabProps) {
  const t = useTranslations();
  const { data, isLoading, isError } = useGetVoicePresetsApi();
  const { playingPresetId, playPreview } = useVoicePresetPreview();

  // SYSTEM 그룹 → USER 그룹 순. 각 그룹 내 seq 오름차순.
  // payload.code 가 visibleVoices 에 없는 카드는 BE config 변경 / Gemini 가시 차단 시
  // pick 해도 매핑 실패라 표시 자체에서 제외.
  const { systemPresets, userPresets } = useMemo(() => {
    const items = data?.data?.items ?? [];
    const visibleCodes = new Set(visibleVoices.map((v) => v.code));
    const sys: VoicePreset[] = [];
    const usr: VoicePreset[] = [];
    items.forEach((p) => {
      if (!visibleCodes.has(p.payload.code)) return;
      if (p.type === DtoUserVoicePresetType.system) sys.push(p);
      else usr.push(p);
    });
    sys.sort((a, b) => a.seq - b.seq);
    usr.sort((a, b) => a.seq - b.seq);
    return { systemPresets: sys, userPresets: usr };
  }, [data, visibleVoices]);

  // dev: caller 가 사전 선택을 의도했지만 화면에 안 보이는 모든 케이스 추적.
  // - stale: BE 에서 삭제됨 (items 자체에 없음)
  // - 필터링됨: payload.code 가 visibleVoices(isOperator/Gemini gating) 에서 제외 — items 에는 있지만 split 결과에서 제거
  // 두 케이스 모두 caller 진입점 (BrandTemplate 등) 의 stale defaultPresetId 신호.
  // 프로덕션에서는 silent (UX 영향 없음).
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (defaultPresetId === undefined) return;
    // 로드 전에는 판단 보류 — false-positive 방지. systemPresets/userPresets 가 derived 라
    // empty 상태와 "전부 필터링됨" 이 구분되지 않으므로 raw items 존재 여부로 판단.
    if (!data?.data?.items) return;
    const found = [...systemPresets, ...userPresets].some(
      (p) => p.id === defaultPresetId,
    );
    if (!found) {
      console.warn(
        `[TtsDialogMyVoiceTab] defaultPresetId=${defaultPresetId} 매칭 카드를 화면에서 찾지 못했습니다 (BE stale 또는 visibleVoices 필터로 제외).`,
      );
    }
  }, [data, defaultPresetId, systemPresets, userPresets]);

  const handlePick = (preset: VoicePreset) => {
    const voice = visibleVoices.find((v) => v.code === preset.payload.code);
    if (!voice) return;
    onPick(presetToTTSVoice(preset, voice));
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-body-medium text-sys-cont-neutral-light">
          {t('MyVoicePage.load-error')}
        </p>
      </div>
    );
  }

  const isEmpty = systemPresets.length === 0 && userPresets.length === 0;
  if (isEmpty) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-body-medium text-sys-cont-neutral-light">
          {t('MyVoicePage.empty-hint')}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-md sm:grid-cols-2 md:grid-cols-3">
      {[...systemPresets, ...userPresets].map((preset) => (
        <VoicePresetCard
          key={preset.id}
          preset={preset}
          mode="pick"
          playingPresetId={playingPresetId}
          isSelected={defaultPresetId === preset.id}
          onPlay={playPreview}
          onSelect={handlePick}
        />
      ))}
    </div>
  );
}
